import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  ArrowRight,
  Clock,
  TrendingUp,
  Target,
  Calendar,
  Lock,
  Mail,
  User,
  Phone,
  Loader2,
  CheckCircle2,
  Zap,
  X,
  AlertCircle,
  Flame,
} from "lucide-react";

import { getCalApi } from "@calcom/embed-react";
import { offerEndLabel, daysLeftLabel } from "../../utils/fomo";
import { toast } from "sonner";
import { PRICING } from "../../data/site";
import RevealPricingSlider from "../shared/RevealPricingSlider";
import { useStripeCheckout } from "../../hooks/useStripeCheckout";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
const STORAGE_KEY = "appfillip_pricing_unlocked";

export default function Pricing() {
  // Homepage always shows USD + Stripe. INR/Razorpay is only on /in country page.
  const { startCheckout, loading: checkoutLoading, error: checkoutError } = useStripeCheckout();
  const handlePlanCheckout = () => startCheckout({ planId: "starter-299" });

  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ namespace: "talk-with-us" });
      cal("ui", {
        hideEventTypeDetails: false,
        layout: "month_view",
      });
    })();
  }, []);

  const handleSliderUnlock = () => setShowModal(true);

  const submitReveal = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, email, and phone / WhatsApp.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("That email doesn't look right.");
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 7) {
      toast.error("Please enter a valid phone / WhatsApp number with country code.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/pricing/reveal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
        }),
      });
      if (!res.ok) throw new Error("Could not unlock. Try again.");
      setUnlocked(true);
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
      toast.success("Pricing unlocked — also sent to your inbox 📩");
      setShowModal(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="pricing"
      className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200 relative overflow-hidden"
      data-testid="pricing-section"
    >
      <div className="absolute inset-0 bg-grid-pattern opacity-50 pointer-events-none" />

      <div className="container relative">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-overline text-electric mb-3">08 — Pricing</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
            Transparent pricing.{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Global-quality</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-electric/30 -z-0" />
            </span>{" "}
            app marketing.
          </h2>
          <p className="mt-5 text-lg text-zinc-600">
            Three packages built around the lifecycle of your app. Cancel
            anytime — though most clients stay 12+ months because the numbers
            keep going up.
          </p>
        </div>

        {/* ── Limited-time FOMO banner ── */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-center gap-3 bg-charcoal text-white px-5 py-3 border-l-4 border-electric">
            <Flame className="w-4 h-4 text-electric flex-shrink-0" />
            <p className="text-sm font-bold tracking-wide">
              <span className="text-electric">LIMITED TIME —</span>{" "}
              50% off launch pricing. We slashed Starter from{" "}
              <span className="line-through text-zinc-400">$599</span> to{" "}
              <span className="text-electric">$299</span>.
              Offer ends <span className="underline decoration-electric">{offerEndLabel()}</span>
              {" · "}<span className="text-yellow-400">{daysLeftLabel()}</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch max-w-4xl mx-auto">
          {PRICING.map((p, idx) => (
            <motion.article
              key={p.tier}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className={`relative flex flex-col ${
                p.highlighted
                  ? "bg-charcoal text-white border border-charcoal md:-translate-y-3 shadow-[10px_10px_0_0_#2196F3]"
                  : "bg-white border border-zinc-200 hover:shadow-[6px_6px_0_0_#2D2D2D] transition-shadow duration-300"
              }`}
              data-testid={`pricing-tier-${p.tier.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {/* Top accent strip */}
              <div
                className="h-1.5 w-full"
                style={{ backgroundColor: p.accent }}
              />

              {/* Badge */}
              <div className="absolute top-1.5 right-0 -translate-y-1/2">
                <span
                  className="text-[10px] font-black uppercase tracking-widest text-white px-3 py-1.5"
                  style={{
                    backgroundColor: p.accent,
                    boxShadow: p.highlighted
                      ? `0 0 18px ${p.accent}cc`
                      : "none",
                  }}
                >
                  {p.badge}
                </span>
              </div>

              <div className="p-8 md:p-10 flex-1 flex flex-col">
                {/* Tier name */}
                <p
                  className={`text-overline mb-3 ${
                    p.highlighted ? "text-electric" : "text-zinc-500"
                  }`}
                >
                  {p.tier}
                </p>

                {/* Price — fully open. Starter shows the price, Custom shows "Talk to Sales". */}
                {(() => {
                  const isCustom = /custom/i.test(p.price?.trim() || "") || /enterprise|custom/i.test(p.tier || "");
                  if (isCustom) {
                    return (
                      <div className="flex items-baseline gap-1 min-h-[60px] md:min-h-[72px]">
                        <span className="font-display text-3xl md:text-4xl font-black tracking-tight">
                          Talk to Sales
                        </span>
                      </div>
                    );
                  }
                  {
                    const usdAmt = parseInt(p.price.replace(/[^0-9]/g,""),10);
                    const dispPrice = p.price;
                    const dispOrig = p.originalPrice || null;
                    const origAmt = parseInt((p.originalPrice || "").replace(/[^0-9]/g,""), 10) || 0;
                    const currAmt = parseInt((p.price || "").replace(/[^0-9]/g,""), 10) || 0;
                    const pct = origAmt && currAmt ? Math.round(((origAmt - currAmt) / origAmt) * 100) : null;
                    return (
                      <div className="min-h-[60px] md:min-h-[72px]">
                        {dispOrig && (
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-sm font-semibold line-through ${p.highlighted ? "text-zinc-500" : "text-zinc-400"}`}>
                              {dispOrig}{p.cadence}
                            </span>
                            {pct && (
                              <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 tracking-wide">
                                SAVE {pct}%
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-baseline gap-1">
                          <span className="font-display text-5xl md:text-6xl font-black tracking-tight">{dispPrice}</span>
                          {p.cadence && (
                            <span className={`text-sm ${p.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>{p.cadence}</span>
                          )}
                        </div>
                        {p.limitedTime && (
                          <div className={`mt-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${p.highlighted ? "text-yellow-400" : "text-orange-500"}`}>
                            <Clock className="w-3 h-3" />
                            Limited time · ends {offerEndLabel()}
                          </div>
                        )}
                      </div>
                    );
                  }
                })()}

                {/* Duration */}
                <div
                  className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold ${
                    p.highlighted ? "text-zinc-400" : "text-zinc-500"
                  }`}
                >
                  <Clock className="w-3.5 h-3.5" style={{ color: p.accent }} />
                  {p.duration}
                </div>

                {/* Description */}
                <p
                  className={`mt-5 text-sm leading-relaxed ${
                    p.highlighted ? "text-zinc-300" : "text-zinc-600"
                  }`}
                >
                  {p.description}
                </p>

                {/* Growth Impact */}
                <div
                  className={`mt-6 border ${
                    p.highlighted
                      ? "border-white/15 bg-white/5"
                      : "border-zinc-200 bg-zinc-50"
                  } p-4`}
                >
                  <div
                    className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest mb-3 ${
                      p.highlighted ? "text-electric" : "text-zinc-500"
                    }`}
                  >
                    <TrendingUp
                      className="w-3 h-3"
                      style={{ color: p.accent }}
                    />
                    Typical Growth Impact
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {p.impact.map((stat) => (
                      <div key={stat.label} className="text-center">
                        <div
                          className="font-display text-xl md:text-2xl font-black leading-tight"
                          style={{ color: p.accent }}
                        >
                          {stat.value}
                        </div>
                        <div
                          className={`text-[10px] uppercase tracking-wider mt-1 leading-tight ${
                            p.highlighted ? "text-zinc-400" : "text-zinc-500"
                          }`}
                        >
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Expected Results */}
                {p.expectedResults && (
                  <div className={`mt-5 border p-4 ${p.highlighted ? "border-white/15 bg-white/5" : "border-zinc-200 bg-zinc-50"}`}>
                    <div className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest mb-3 ${p.highlighted ? "text-electric" : "text-zinc-500"}`}>
                      <TrendingUp className="w-3 h-3" style={{ color: p.accent }} />
                      Expected Results
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      {p.expectedResults.map((r) => (
                        <div key={r.label}>
                          <div className="font-display text-lg font-black leading-tight" style={{ color: p.accent }}>
                            {r.metric}
                          </div>
                          <div className={`text-[10px] uppercase tracking-wide mt-0.5 leading-tight ${p.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>
                            {r.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 text-sm leading-relaxed"
                    >
                      <span
                        className="w-4 h-4 mt-0.5 flex-shrink-0 grid place-items-center"
                        style={{ backgroundColor: `${p.accent}22` }}
                      >
                        <Check
                          className="w-3 h-3"
                          style={{ color: p.accent }}
                          strokeWidth={3.5}
                        />
                      </span>
                      <span
                        className={
                          p.highlighted ? "text-zinc-200" : "text-zinc-700"
                        }
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Ideal for */}
                <div
                  className={`mt-7 pt-5 border-t ${
                    p.highlighted ? "border-white/10" : "border-zinc-200"
                  }`}
                >
                  <div
                    className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest mb-2 ${
                      p.highlighted ? "text-zinc-500" : "text-zinc-400"
                    }`}
                  >
                    <Target
                      className="w-3 h-3"
                      style={{ color: p.accent }}
                    />
                    Ideal for
                  </div>
                  <p
                    className={`text-xs leading-relaxed ${
                      p.highlighted ? "text-zinc-300" : "text-zinc-600"
                    }`}
                  >
                    {p.idealFor}
                  </p>
                </div>

                {/* CTA — Enterprise opens Cal.com booking; others scroll to audit */}
                {(() => {
                  const tierKey = (p.tier || "").toLowerCase();
                  const isEnterprise = tierKey.includes("enterprise") || /^custom$/i.test(p.price?.trim() || "");
                  const cls = `mt-8 inline-flex items-center justify-center gap-2 px-5 py-3.5 font-bold transition-all group ${
                    p.highlighted
                      ? "bg-electric text-white hover:bg-electric-400"
                      : "bg-charcoal text-white hover:bg-electric"
                  }`;
                  if (isEnterprise) {
                    return (
                      <button
                        type="button"
                        data-cal-namespace="talk-with-us"
                        data-cal-link="appfillip/talk-with-us"
                        data-cal-config='{"layout":"month_view"}'
                        data-testid={`pricing-cta-${p.tier.toLowerCase().replace(/\s+/g, "-")}`}
                        aria-label={`Talk to Sales — AppFillip ${p.tier} plan`}
                        className={cls}
                      >
                        {p.cta || "Talk to Sales"}
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </button>
                    );
                  }
                  return (
                    <div>
                      <button
                        type="button"
                        disabled={checkoutLoading}
                        onClick={handlePlanCheckout}
                        data-testid={`pricing-cta-${p.tier.toLowerCase().replace(/\s+/g, "-")}`}
                        aria-label={`${p.cta} — AppFillip ${p.tier} plan`}
                        className={`${cls} disabled:opacity-60 disabled:cursor-not-allowed`}
                      >
                        {checkoutLoading ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                        ) : (
                          <>{p.cta}<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>
                        )}
                      </button>
                      {p.limitedTime && (
                        <p className={`mt-2 text-center text-[11px] ${p.highlighted ? "text-zinc-400" : "text-zinc-400"}`}>
                          🔒 Price goes back to $599 after {offerEndLabel()}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            </motion.article>
          ))}
        </div>

        {/* Checkout error */}
        {checkoutError && (
          <div className="mt-6 max-w-lg mx-auto flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{checkoutError}</span>
          </div>
        )}

        {/* Helper: indie-friendly intro line under the 2 cards */}
        <div className="mt-12 text-center">
          <p className="text-sm text-zinc-600 max-w-2xl mx-auto">
            Indie or solo? Start at{" "}
            <strong className="text-charcoal">$299/month</strong>{" "}
            with a 3-month commitment. Funded or scaling?{" "}
            <button
              type="button"
              data-cal-namespace="talk-with-us"
              data-cal-link="appfillip/talk-with-us"
              data-cal-config='{"layout":"month_view"}'
              className="text-electric font-bold hover:underline"
            >
              Talk to Sales →
            </button>
          </p>
        </div>

        {/* Talk to expert — animated CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-14 flex flex-col items-center justify-center gap-5 text-center"
        >
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-zinc-300" />
            <p className="text-zinc-500 text-sm font-medium tracking-wide">
              Have questions or need a customised plan?
            </p>
            <span className="h-px w-12 bg-zinc-300" />
          </div>

          <div className="relative">
            {/* Animated ping ring */}
            <motion.span
              className="absolute inset-0 rounded-none bg-electric/20"
              animate={{ scale: [1, 1.18, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            />
            {/* Shimmer sweep */}
            <motion.button
              data-cal-namespace="talk-with-us"
              data-cal-link="appfillip/talk-with-us"
              data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
              data-testid="talk-with-expert-cta"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              className="relative group inline-flex items-center gap-2.5 overflow-hidden bg-charcoal text-white px-7 py-3.5 text-sm font-bold tracking-wide shadow-[4px_4px_0_0_#2196F3] hover:shadow-[6px_6px_0_0_#2196F3] transition-shadow duration-300"
            >
              {/* shimmer */}
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                initial={{ x: "-100%" }}
                animate={{ x: "200%" }}
                transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
              />
              <Calendar className="w-4 h-4 text-electric" />
              Talk with Expert
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1.5" />
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* ─── Reveal pricing modal ─── */}
      <AnimatePresence>
        {showModal && (
          <div
            className="fixed inset-0 z-[80] grid place-items-center px-4"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={() => !loading && setShowModal(false)}
              className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm"
            />
            <motion.form
              onSubmit={submitReveal}
              initial={{ opacity: 0, y: 12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.97 }}
              transition={{ duration: 0.25 }}
              className="relative w-full max-w-md bg-white border border-charcoal shadow-[10px_10px_0_0_#2196F3]"
            >
              <button
                type="button"
                onClick={() => !loading && setShowModal(false)}
                className="absolute top-4 right-4 text-zinc-400 hover:text-charcoal z-10"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-7 md:p-9">
                <div className="w-12 h-12 grid place-items-center bg-electric mb-5 shadow-[3px_3px_0_0_#2D2D2D]">
                  <Lock className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <p className="text-overline text-electric mb-2">Unlock Pricing</p>
                <h3 className="font-display text-2xl font-extrabold text-charcoal leading-tight">
                  Where should we send the breakdown?
                </h3>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                  We'll show all three prices instantly and email a detailed PDF-quality
                  breakdown to your inbox.
                </p>

                <div className="mt-6 space-y-4">
                  <label className="block">
                    <span className="text-overline text-zinc-500">Full Name</span>
                    <div className="relative mt-2">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                        placeholder="Alex Johnson"
                        className="w-full border border-zinc-300 px-4 py-3 text-sm focus:border-electric outline-none pl-10"
                        autoFocus
                      />
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-overline text-zinc-500">Work Email</span>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                        placeholder="you@yourapp.com"
                        className="w-full border border-zinc-300 px-4 py-3 text-sm focus:border-electric outline-none pl-10"
                      />
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-overline text-zinc-500">Phone / WhatsApp</span>
                    <div className="relative mt-2">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                        placeholder="+1 415 555 1234"
                        className="w-full border border-zinc-300 px-4 py-3 text-sm focus:border-electric outline-none pl-10"
                        data-testid="pricing-reveal-phone"
                      />
                    </div>
                    <span className="mt-1.5 block text-[11px] text-zinc-500">
                      Include country code · we'll WhatsApp you the strategy notes too.
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-electric text-white px-6 py-3.5 font-bold text-sm hover:bg-electric-400 transition-colors disabled:opacity-60"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Unlocking…</>
                  ) : (
                    <>Unlock Pricing & Email Me <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>

                {/* Audit CTA in the modal */}
                <Link
                  to="/#audit"
                  onClick={() => setShowModal(false)}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 text-charcoal px-6 py-3 font-semibold text-sm transition-colors"
                >
                  <Zap className="w-4 h-4 text-electric" />
                  Get a free App Audit Report instead
                </Link>

                <p className="mt-4 text-[11px] text-zinc-500 text-center">
                  No spam. Pricing email arrives in under 30 seconds.
                </p>

                {/* Social proof */}
                <div className="mt-5 pt-4 border-t border-zinc-100 text-center">
                  <div className="inline-flex items-center justify-center gap-1.5 mb-1.5" aria-hidden>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <svg key={i} className="w-3 h-3 fill-electric text-electric" viewBox="0 0 24 24">
                        <polygon points="12 2 15 8.5 22 9.5 17 14.5 18.5 22 12 18.5 5.5 22 7 14.5 2 9.5 9 8.5" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-[11px] text-zinc-600 leading-relaxed">
                    Trusted by startups backed by the world's best investors.
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                    Sequoia · a16z · Accel · YC · Lightspeed · Tiger
                  </p>
                </div>
              </div>
            </motion.form>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
